import React, { useRef, useState, useEffect, useMemo } from "react";
import orderBy from "lodash/orderBy";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import uniqBy from "lodash/uniqBy";
import debounce from "lodash/debounce";
import { useQuery } from "@apollo/client/react";

import useAuth from "../../../../hooks/useAuth";
import ScanditScanner from "../../../../components/barcode/ScanditScanner/ScanditScanner";
import WarehouseModal from "../WarehouseModal";
import PopperComponent from "./PopperComponent";
import PaperComponent from "./PaperComponent";
import SelectFixedLocation from "./SelectFixedLocation";
import LocationOption from "./LocationOption";
import SelectLocationPositionModal from "./SelectLocationPositionModal";
import SelectWarehouseModal from "./SelectWarehouseModal";
import useWarehouseLocations, { ITEMS_PER_PAGE } from "./hooks/useWarehouseLocations";
import useSelectedHiddenLocations from "./hooks/useSelectedHiddenLocations";
import ConfirmRemoveMobileLocationModal from "./ConfirmRemoveMobileLocationModal";
import { WAREHOUSE_NAMES } from "../../../../constants/graphql/queries";
import {
  getGroupedLocations,
  formatWarehouseLocations,
  formatLocation,
  getInputLabel,
} from "./utils";
import { formatRackLocationLabel } from "src/utils/rackLocations";

const VIRTUAL_PALLET_LOCATION = "Virtual Pallet Location";

export default function StorageLocationAutocomplete({
  index,
  autoOpen = false,
  value,
  disabled,
  warehouseMapFilters,
  triggerScan,
  setTriggerScan,
  onChange,
  projectId,
  warehouseId,
  locationsVariables = { matchLocationName: true, active: true, expandSlotSiblings: true },
  enableMobileWarehouseModal = true,
}) {
  const { user } = useAuth();
  const autocompleteRef = useRef();
  const listboxRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const requestedPagesRef = useRef(new Set());
  const [inputValue, setInputValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [selectLocationPosition, setSelectLocationPosition] = useState(null);
  const [openWarehouse, setOpenWarehouse] = useState(false);
  const [openScan, setOpenScan] = useState(false);
  const [openFixedLocation, setOpenFixedLocation] = useState(false);
  const [selectedMobileLocation, setSelectedMobileLocation] = useState(null);
  const [openVirtualPalletLocation, setOpenVirtualPalletLocation] = useState(false);
  const [confirmMobileLocationRemoval, setConfirmMobileLocationRemoval] = useState(null);
  const [includeInactiveLocations, setIncludeInactiveLocations] = useState(false);
  const { active, ...locationVariables } = locationsVariables;
  const canSearchInactiveLocations = active === true;
  const variables = {
    limit: ITEMS_PER_PAGE,
    search: debouncedSearchValue,
    ...locationVariables,
    ...(includeInactiveLocations || active === undefined ? {} : { active }),
    ...(projectId ? { projectId } : {}),
    ...(warehouseId ? { warehouseId } : {}),
  };
  const variablesKey = JSON.stringify(variables);
  const { data, loading, fetchMore, error } = useWarehouseLocations({
    variables: { ...variables, page: 1 },
  });
  const warehouseLocations = formatWarehouseLocations(data);
  const { data: { warehouses = [] } = {} } = useQuery(WAREHOUSE_NAMES, {
    variables: { userWarehouses: true },
  });

  const number = typeof index === "number" ? index + 1 : "";
  const groupedLocations = getGroupedLocations(warehouseLocations);
  const selectedHiddenLocations = useSelectedHiddenLocations(value);

  const allLocations = uniqBy(
    orderBy(groupedLocations, v => v.locationId)
      .map(formatLocation)
      .concat(selectedHiddenLocations),
    item => `${item.locationName}-${item.warehouseId}`
  );

  const currentPage = data?.warehouseLocationsCollection?.metadata?.currentPage;
  const totalPages = data?.warehouseLocationsCollection?.metadata?.totalPages;
  const hasMorePages = currentPage < totalPages;

  const [open, setOpen] = useState(autoOpen);
  const closePopper = () => setOpen(false);
  const openPopper = () => setOpen(true);

  const showInactiveSearchOption =
    canSearchInactiveLocations &&
    !includeInactiveLocations &&
    debouncedSearchValue &&
    !loading &&
    !error &&
    allLocations.length === 0;

  const noOptionsText = showInactiveSearchOption ? (
    <Box p={2} textAlign="center">
      <Typography variant="body2">No active storage locations found.</Typography>
      <Button
        color="primary"
        onMouseDown={event => event.preventDefault()}
        onClick={() => setIncludeInactiveLocations(true)}
      >
        Search inactive locations
      </Button>
    </Box>
  ) : (
    "No options"
  );

  const selectedValue = useMemo(() => {
    const locationIds = (value.warehouse_locations || []).map(loc => String(loc.id));
    const list = allLocations.filter(
      v => value.warehouse?.warehouse_id === v.warehouseId && locationIds.includes(String(v.id))
    );

    if (value.mobile_warehouse_location?.id) {
      return list.map(v => ({
        ...v,
        mobile_warehouse_location: value.mobile_warehouse_location,
      }));
    }

    return list;
  }, [allLocations, value.warehouse, value.warehouse_locations, value.mobile_warehouse_location]);

  const mergeSelectedLocations = locations =>
    uniqBy(
      [...selectedValue, ...locations],
      location => String(location.id || location.locationId || location.location_id || location.locationName)
    );

  const handleToggle = () => setOpenWarehouse(v => !v);
  const handleToggleScan = () => setOpenScan(v => !v);

  const handleCloseScan = () => setOpenScan(false);

  // Helper to normalize warehouse object to snake_case keys (backend expects snake_case)
  const normalizeWarehouse = warehouse => {
    if (!warehouse) return null;
    return {
      id: warehouse.id,
      warehouse_id: warehouse.warehouseId || warehouse.warehouse_id,
      name: warehouse.name,
    };
  };

  const normalizeWarehouseLocation = location => ({
    id: location?.id,
    location_id: location?.locationId || location?.location_id,
    location_name: location?.locationName || location?.location_name,
    zone: location?.zone,
  });

  const buildLocationChangeData = (locations, warehouse, extra = {}) => ({
    warehouse: normalizeWarehouse(warehouse),
    warehouse_locations: uniqBy(
      locations.map(normalizeWarehouseLocation),
      location => String(location.id || location.location_id || location.location_name)
    ),
    ...extra,
  });

  const handleSelect = ({ __typename: type, ...locationData }) => {
    const isMobileWarehouseLocation = type === "MobileWarehouseLocation";
    const isWarehouseLocation = type === "WarehouseLocation";

    if (isWarehouseLocation && locationData.name === VIRTUAL_PALLET_LOCATION) {
      setOpenVirtualPalletLocation(true);
      return;
    }

    if (enableMobileWarehouseModal && isMobileWarehouseLocation) {
      if (
        locationData.permanentWarehouseLocationAssignment &&
        locationData.warehouseLocations?.length > 0
      ) {
        // Use first warehouse location for nested structure
        const firstWarehouseLocation = locationData.warehouseLocations[0];
        const changeData = {
          warehouse: normalizeWarehouse(locationData.warehouse),
          warehouse_locations: [firstWarehouseLocation],
          mobile_warehouse_location: {
            id: locationData.id,
            location_id: locationData.locationId,
            location_name: locationData.locationName,
          },
        };

        onChange(changeData, locationData);
        return;
      }

      setSelectedMobileLocation(locationData);
      setOpenFixedLocation(true);
      return;
    }

    // Build nested structure with full objects.
    // When multiple positions were selected (e.g. "All Positions" from the
    // warehouse map), locationData.locations contains all of them.
    const locations = locationData.locations || [locationData];
    onChange(
      buildLocationChangeData(mergeSelectedLocations(locations), locationData.warehouse, {
        ...(value.mobile_warehouse_location?.id && {
          mobile_warehouse_location: value.mobile_warehouse_location,
        }),
      }),
      locationData
    );
  };

  const handleDetectedCode = code => {
    setTimeout(() => {
      autocompleteRef.current.click();
      openPopper();
      setIncludeInactiveLocations(false);
      setInputValue(code);
      debouncedSetSearch(code);
    });
  };

  const debouncedSetSearch = useMemo(
    () =>
      debounce(value => {
        setDebouncedSearchValue(value);
      }, 300),
    []
  );

  // Cleanup debounce on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (debouncedSetSearch && typeof debouncedSetSearch.cancel === "function") {
        debouncedSetSearch.cancel();
      }
    };
  }, [debouncedSetSearch]);

  const handleInputChange = (event, newValue) => {
    if (event) {
      setIncludeInactiveLocations(false);
      setInputValue(newValue);
      debouncedSetSearch(newValue);
    }
  };

  const handleChange = (event, newValue, reason) => {
    const addedItem = newValue.length > selectedValue.length ? newValue.at(-1) : null;
    const type = addedItem?.__typename;
    const isMobileWarehouseLocation = type === "MobileWarehouseLocation";
    const isWarehouseLocation = type === "WarehouseLocation";

    if (isWarehouseLocation && addedItem?.name === VIRTUAL_PALLET_LOCATION) {
      setOpenVirtualPalletLocation(true);
      return;
    }

    if (enableMobileWarehouseModal && isMobileWarehouseLocation && addedItem) {
      if (
        addedItem.permanentWarehouseLocationAssignment &&
        addedItem.warehouseLocations?.length > 0
      ) {
        const firstWarehouseLocation = addedItem.warehouseLocations[0];
        const locationData = {
          warehouse: normalizeWarehouse(addedItem.warehouse),
          warehouse_locations: [firstWarehouseLocation],
          mobile_warehouse_location: {
            id: addedItem.id,
            location_id: addedItem.locationId,
            location_name: addedItem.locationName,
          },
        };
        onChange(locationData, addedItem);
        return;
      }
      setSelectedMobileLocation(addedItem);
      setOpenFixedLocation(true);
      return;
    }

    const locationData = newValue.length
      ? buildLocationChangeData(newValue, newValue[0]?.warehouse, {
          ...(value.mobile_warehouse_location?.id && {
            mobile_warehouse_location: value.mobile_warehouse_location,
          }),
        })
      : {
          warehouse: null,
          warehouse_locations: [],
        };

    if (reason === "removeOption" || reason === "clear") {
      if (value.mobile_warehouse_location?.id) {
        setConfirmMobileLocationRemoval([locationData, newValue[0]]);
        return;
      }
      openPopper();
      setTimeout(() => {
        const input = autocompleteRef.current.querySelector("input");
        if (input) {
          input.focus();
        }
      });
    }

    onChange(locationData, newValue[0]);
  };

  const handleConfirmRemoveMobileLocation = () => {
    const [locationData, mobileWarehouseLocation] = confirmMobileLocationRemoval;
    locationData.mobile_warehouse_location = null;
    onChange(locationData, mobileWarehouseLocation);
    setConfirmMobileLocationRemoval(null);
    openPopper();
  };

  const handleCancelRemoveMobileLocation = () => {
    const [locationData, mobileWarehouseLocation] = confirmMobileLocationRemoval;
    locationData.mobile_warehouse_location = value.mobile_warehouse_location;
    onChange(locationData, mobileWarehouseLocation);
    setConfirmMobileLocationRemoval(null);
    openPopper();
  };

  const handleSelectFixedLocation = (data, mobileWarehouseLocation) => {
    onChange({
      warehouse: normalizeWarehouse(data[0].warehouse),
      warehouse_locations: data.map(loc => ({
        id: loc.id,
        location_id: loc.locationId || loc.location_id,
        location_name: loc.locationName || loc.location_name,
        zone: loc.zone,
      })),
      mobile_warehouse_location: {
        id: mobileWarehouseLocation.id,
        location_id: mobileWarehouseLocation.locationId,
        location_name: mobileWarehouseLocation.locationName || mobileWarehouseLocation.location_name,
      },
    });
    setSelectedMobileLocation(null);
    setOpenFixedLocation(false);
  };

  const handleSelectVirtualPalletLocation = value => {
    setSelectedMobileLocation(value);
    setOpenFixedLocation(true);
  };

  const applySelectedLocations = (locations, extra = {}) => {
    const firstLocation = locations[0];
    if (!firstLocation) return;

    onChange(
      buildLocationChangeData(mergeSelectedLocations(locations), firstLocation.warehouse, {
        ...(value.mobile_warehouse_location?.id && {
          mobile_warehouse_location: value.mobile_warehouse_location,
        }),
      }),
      extra
    );
  };

  const handleSelectGroupedLocations = locations => {
    setSelectLocationPosition(locations);
  };

  const handleSelectLocation = locations => {
    applySelectedLocations(locations.length ? locations : [locations[0]], { locations });
    setSelectLocationPosition(null);
  };

  const handleFetchMore = values => {
    const page = values.page;

    if (page && requestedPagesRef.current.has(page)) {
      return Promise.resolve();
    }

    if (page) {
      requestedPagesRef.current.add(page);
    }

    return fetchMore({
      variables: {
        ...variables,
        ...values,
      },
      updateQuery: (prevResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prevResult;
        return {
          warehouseLocationsCollection: {
            ...prevResult.warehouseLocationsCollection,
            collection: uniqBy(
              [
                ...prevResult.warehouseLocationsCollection.collection,
                ...fetchMoreResult.warehouseLocationsCollection.collection,
              ],
              v => v.id
            ),
            metadata: fetchMoreResult.warehouseLocationsCollection.metadata,
          },
        };
      },
    }).catch(error => {
      if (page) {
        requestedPagesRef.current.delete(page);
      }
      throw error;
    });
  };

  const handleLoadMore = () => {
    if (listboxRef.current) {
      scrollPositionRef.current = listboxRef.current.scrollTop;
    }

    if (hasMorePages && !loading) {
      handleFetchMore({
        page: data.warehouseLocationsCollection.metadata.currentPage + 1,
      });
    }
  };

  useEffect(() => {
    requestedPagesRef.current = new Set();
  }, [variablesKey]);

  useEffect(() => {
    if (listboxRef.current && scrollPositionRef.current > 0) {
      listboxRef.current.scrollTop = scrollPositionRef.current;
    }
  }, [warehouseLocations]);

  useEffect(() => {
    if (triggerScan) {
      handleToggleScan();
      setTriggerScan(false);
    }
  }, [triggerScan]);

  return (
    <>
      {selectLocationPosition && (
        <SelectLocationPositionModal
          open={!!selectLocationPosition}
          onClose={() => setSelectLocationPosition(null)}
          onSubmit={handleSelectLocation}
          locations={selectLocationPosition}
        />
      )}
      {openVirtualPalletLocation && (
        <SelectWarehouseModal
          open={openVirtualPalletLocation}
          onClose={() => setOpenVirtualPalletLocation(false)}
          onSelect={handleSelectVirtualPalletLocation}
          projectId={projectId}
        />
      )}
      {openFixedLocation && (
        <SelectFixedLocation
          location={selectedMobileLocation}
          onClose={() => setOpenFixedLocation(false)}
          onSubmit={handleSelectFixedLocation}
        />
      )}
      {confirmMobileLocationRemoval && (
        <ConfirmRemoveMobileLocationModal
          onClose={handleCancelRemoveMobileLocation}
          onConfirm={handleConfirmRemoveMobileLocation}
          mobileName={value.mobile_warehouse_location}
        />
      )}
      <Autocomplete
        ref={autocompleteRef}
        size="large"
        multiple
        fullWidth
        disabled={disabled}
        options={allLocations.concat(
          hasMorePages || loading ? Array.from({ length: 5 }, () => "loading") : []
        )}
        value={selectedValue}
        inputValue={inputValue}
        loading={loading}
        open={open}
        onOpen={() => {
          openPopper();
        }}
        onClose={closePopper}
        onInputChange={handleInputChange}
        onChange={handleChange}
        noOptionsText={noOptionsText}
        filterOptions={options => options}
        getOptionLabel={v =>
          formatRackLocationLabel({
            mobile_warehouse_location: v.mobile_warehouse_location,
            warehouse_locations: [{ location_name: v.locationName }],
          })
        }
        renderOption={(props, option) => (
          <LocationOption
            props={props}
            option={option}
            onSelectLocationPosition={handleSelectGroupedLocations}
          />
        )}
        getOptionDisabled={v =>
          selectedValue.length > 0 && selectedValue[0].warehouseId !== v.warehouseId
        }
        renderInput={params => (
          <TextField
            {...params}
            label={getInputLabel(number, value, warehouses)}
            autoFocus={autoOpen}
          />
        )}
        ListboxProps={{
          style: { paddingBottom: 0 },
          position: "bottom-start",
          ref: listboxRef,
          onScroll: e => {
            if (e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight < 100) {
              handleLoadMore();
            }
          },
        }}
        PopperComponent={PopperComponent}
        PaperComponent={props => (
          <PaperComponent
            {...props}
            onToggle={handleToggle}
            onToggleScan={handleToggleScan}
          />
        )}
      />
      {openWarehouse && (
        <WarehouseModal
          open={openWarehouse}
          warehouseMapFilters={warehouseMapFilters}
          onClose={handleToggle}
          onSelect={handleSelect}
        />
      )}
      {openScan && (
        <ScanditScanner
          onDetected={handleDetectedCode}
          onClose={handleCloseScan}
        />
      )}
    </>
  );
}
